import useTranslation from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import type { MediaTemplateCategoryCount } from '@/constants/mediaTemplates';

interface TemplateCategoryFilterProps {
  categories: MediaTemplateCategoryCount[];
  /** `null` = "All". */
  value: string | null;
  onChange: (category: string | null) => void;
  total: number;
  variant?: 'light' | 'dark';
  className?: string;
}

// One horizontally scrollable row of category chips. It scrolls instead of
// wrapping so the grid below never jumps when the chip count changes, and the
// negative margin lets chips bleed to the screen edge on phones.
const TemplateCategoryFilter = ({
  categories,
  value,
  onChange,
  total,
  variant = 'light',
  className,
}: TemplateCategoryFilterProps) => {
  const { t } = useTranslation('media-templates');

  if (categories.length < 2) return null;

  const chips: Array<{ key: string | null; label: string; count: number }> = [
    { key: null, label: t('All'), count: total },
    ...categories.map(({ category, count }) => ({
      key: category,
      label: t(category),
      count,
    })),
  ];

  return (
    <div
      role='tablist'
      aria-label={t('Template categories')}
      className={clsxm(
        '-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {chips.map((chip) => {
        const isActive = chip.key === value;
        return (
          <button
            key={chip.key ?? 'all'}
            type='button'
            role='tab'
            aria-selected={isActive}
            onClick={() => onChange(chip.key)}
            className={clsxm(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              variant === 'dark'
                ? isActive
                  ? 'border-white bg-white text-slate-900'
                  : 'border-white/15 bg-white/5 text-slate-300 hover:border-white/40 hover:text-white'
                : isActive
                  ? 'border-gray-900 bg-gray-900 text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-100',
            )}
          >
            {chip.label}
            <span
              className={clsxm(
                'tabular-nums',
                isActive ? 'opacity-70' : 'opacity-50',
              )}
            >
              {chip.count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default TemplateCategoryFilter;
